<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { formatMonetaryValue, parseMonetaryValue } from './helpers';
import CardPriorityIcon from '../Conversation/ConversationCard/CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  labelId: {
    type: [String, Number],
    default: null,
  },
  index: {
    type: Number,
    default: null,
  },
  isDragging: {
    type: Boolean,
    default: false,
  },
  visibleFields: {
    type: Array,
    default: () => [],
  },
  monetaryAttributeKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['dragstart', 'dragend']);

const route = useRoute();
const router = useRouter();
const accountId = computed(() => route.params.accountId);

const contactName = computed(() => {
  return (
    props.conversation.meta?.contact?.name ||
    props.conversation.meta?.sender?.name ||
    'Usuário anônimo'
  );
});

const inboxName = computed(() => {
  return props.conversation.inbox?.name || 'Inbox';
});

const lastMessage = computed(() => {
  const messages = props.conversation.messages || [];
  if (messages.length === 0) return '';
  const lastMsg = messages[messages.length - 1];
  return lastMsg.content ? lastMsg.content.substring(0, 100) : '';
});

const unreadCount = computed(() => {
  return props.conversation.unread_count || 0;
});

const shouldShowField = field =>
  computed(() => props.visibleFields.includes(field));

const showLastMessage = shouldShowField('lastMessage');
const showInbox = shouldShowField('inbox');
const showCreatedAt = shouldShowField('createdAt');
const showUnreadCount = shouldShowField('unreadCount');

const monetaryValue = computed(() => {
  if (!props.monetaryAttributeKey) {
    return '';
  }

  const value =
    props.conversation.custom_attributes?.[props.monetaryAttributeKey] ||
    props.conversation.customAttributes?.[props.monetaryAttributeKey];

  if (value === null || value === undefined || value === '') {
    return '';
  }

  return formatMonetaryValue(parseMonetaryValue(value));
});

const handleDragStart = e => {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/plain', props.conversation.id);
  if (props.labelId) {
    e.dataTransfer.setData('kanban/fromLabelId', props.labelId);
  }
  if (props.index !== null && props.index !== undefined) {
    e.dataTransfer.setData('kanban/fromIndex', props.index);
  }
  emit('dragstart');
};

const handleDragEnd = () => {
  emit('dragend');
};

const handleCardClick = () => {
  if (props.isDragging) {
    return;
  }
  const accountIdValue = accountId.value || props.conversation.account_id;
  if (!accountIdValue) {
    return;
  }
  router.push(
    frontendURL(
      conversationUrl({
        accountId: accountIdValue,
        id: props.conversation.id,
      })
    )
  );
};
</script>

<template>
  <div
    class="cursor-move rounded-lg border border-n-weak bg-white p-3 shadow-sm transition-[background-color,box-shadow] hover:bg-n-alpha-1 hover:shadow-md dark:bg-n-solid-2 dark:hover:bg-n-alpha-2"
    :class="{ 'opacity-50': isDragging }"
    draggable="true"
    role="button"
    tabindex="0"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @click="handleCardClick"
  >
    <!-- Header com nome e unread count -->
    <div class="flex items-start justify-between mb-2">
      <div class="flex min-w-0 flex-1 items-start gap-2">
        <CardPriorityIcon
          v-if="conversation.priority"
          :priority="conversation.priority"
        />
        <h3 class="flex-1 truncate text-sm font-semibold text-n-slate-12">
          {{ contactName }}
        </h3>
      </div>
      <span
        v-if="showUnreadCount && unreadCount > 0"
        class="ml-2 inline-flex items-center justify-center rounded-full bg-n-ruby-3 px-2 py-0.5 text-xs font-semibold text-n-ruby-11"
      >
        {{ unreadCount }}
      </span>
    </div>

    <!-- Última mensagem -->
    <p
      v-if="showLastMessage && lastMessage"
      class="mb-2 line-clamp-2 text-xs text-n-slate-10"
    >
      {{ lastMessage }}
    </p>

    <div
      v-if="monetaryValue"
      class="mb-2 rounded-lg bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-12"
    >
      <span>{{ monetaryValue }}</span>
    </div>

    <!-- Footer com inbox e timestamp -->
    <div class="flex items-center justify-between text-xs text-n-slate-10">
      <span v-if="showInbox" class="truncate">{{ inboxName }}</span>
      <span v-else />
      <span v-if="showCreatedAt" class="ml-2 text-n-slate-10">
        {{ new Date(conversation.created_at).toLocaleDateString() }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
